import { alpha, createTheme } from '@mui/material/styles';

/** Design tokens: dark trading terminal. Components read these instead of raw hex. */
export const t = {
  bg: '#0B1120',
  sidebar: '#080D19',
  panel: '#0F172A',
  panel2: '#131C31',
  border: '#1E293B',
  hover: '#17223A',
  text: '#E2E8F0',
  text2: '#94A3B8',
  text3: '#64748B',
  accent: '#6366F1',
  accentSoft: '#818CF8',
  profit: '#4ADE80',      // text on dark (contrast > 7:1)
  loss: '#F87171',        // text on dark (contrast > 5:1)
  profitMark: '#1FAF55',  // chart marks, CVD-validated pair on #0F172A
  lossMark: '#C0262D',
  warn: '#FBBF24',
  danger: '#DC2626',
  mono: '"JetBrains Mono", "SFMono-Regular", Consolas, monospace',
};

export const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: { main: t.accent, light: t.accentSoft, contrastText: '#fff' },
    success: { main: '#22C55E' },
    error: { main: t.danger },
    warning: { main: '#F59E0B' },
    info: { main: '#38BDF8' },
    background: { default: t.bg, paper: t.panel },
    text: { primary: t.text, secondary: t.text2, disabled: t.text3 },
    divider: t.border,
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    h4: { fontWeight: 700, letterSpacing: '-0.02em' },
    h5: { fontWeight: 700, letterSpacing: '-0.01em' },
    h6: { fontWeight: 600, fontSize: '1rem' },
    subtitle2: { fontWeight: 600 },
    overline: { fontWeight: 600, letterSpacing: '0.08em', lineHeight: 1.6 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: { backgroundColor: t.bg, fontVariantNumeric: 'tabular-nums' },
        '*::-webkit-scrollbar': { width: 8, height: 8 },
        'input::-webkit-calendar-picker-indicator': { filter: 'invert(0.75)', cursor: 'pointer' },
        '*::-webkit-scrollbar-thumb': { background: t.border, borderRadius: 8 },
        '@media (prefers-reduced-motion: reduce)': { '*': { transition: 'none !important', animation: 'none !important' } },
      },
    },
    MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { border: `1px solid ${t.border}`, backgroundColor: t.panel } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: { root: { borderRadius: 8, minHeight: 38 }, containedPrimary: { '&:hover': { backgroundColor: '#5558E3' } } },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600, borderRadius: 6 }, sizeSmall: { height: 24 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: t.panel2,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: t.border },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#334155' },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: t.border, padding: '10px 12px' },
        head: { color: t.text3, fontSize: '0.72rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' },
      },
    },
    MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: t.hover } } } },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderColor: t.border, color: t.text2, fontWeight: 600, padding: '6px 14px',
          '&.Mui-selected': { backgroundColor: alpha(t.accent, 0.18), color: t.text, '&:hover': { backgroundColor: alpha(t.accent, 0.25) } },
        },
      },
    },
    MuiAlert: { styleOverrides: { root: { borderRadius: 10, alignItems: 'center' } } },
    MuiDialog: { styleOverrides: { paper: { border: `1px solid ${t.border}` } } },
    MuiLinearProgress: { styleOverrides: { root: { height: 6, borderRadius: 3, backgroundColor: t.panel2 }, bar: { borderRadius: 3 } } },
    MuiTooltip: { styleOverrides: { tooltip: { backgroundColor: '#1E293B', border: `1px solid #334155`, fontSize: '0.8rem' } } },
  },
});
